// Turn sparse spot-elevation points into an interpolated elevation field
// (inverse-distance weighting) and extract iso-contour lines (marching squares).
import { WORLD_W, WORLD_H } from "./blueprint";

export function buildElevationField(points, { w = WORLD_W, h = WORLD_H, cell = 2, power = 2 } = {}) {
  const pts = (points || []).filter((p) => Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)));
  if (pts.length < 3) return null;
  const cols = Math.floor(w / cell) + 1;
  const rows = Math.floor(h / cell) + 1;
  const grid = [];
  let min = Infinity, max = -Infinity;
  for (let j = 0; j < rows; j++) {
    const row = [];
    for (let i = 0; i < cols; i++) {
      const gx = i * cell, gy = j * cell;
      let num = 0, den = 0, exact = null;
      for (const p of pts) {
        const dx = gx - p.x, dy = gy - p.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 1e-6) { exact = Number(p.z) || 0; break; }
        const wgt = 1 / Math.pow(d2, power / 2);
        num += wgt * (Number(p.z) || 0); den += wgt;
      }
      const v = exact !== null ? exact : (den ? num / den : 0);
      row.push(v);
      if (v < min) min = v;
      if (v > max) max = v;
    }
    grid.push(row);
  }
  return { grid, cols, rows, cell, min, max };
}

export function contourLevels(min, max, count = 6) {
  if (max <= min) return [];
  const step = (max - min) / count;
  const levels = [];
  for (let i = 1; i < count; i++) levels.push(min + step * i);
  return levels;
}

export function contourLines(field, levels) {
  const { grid, cols, rows, cell } = field;
  const interp = (x1, y1, v1, x2, y2, v2, lvl) => {
    const t = (lvl - v1) / ((v2 - v1) || 1e-9);
    return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
  };
  const out = [];
  for (const lvl of levels) {
    const segs = [];
    for (let j = 0; j < rows - 1; j++) {
      for (let i = 0; i < cols - 1; i++) {
        const x0 = i * cell, y0 = j * cell, x1 = (i + 1) * cell, y1 = (j + 1) * cell;
        const tl = grid[j][i], tr = grid[j][i + 1], br = grid[j + 1][i + 1], bl = grid[j + 1][i];
        let idx = 0;
        if (tl > lvl) idx |= 8;
        if (tr > lvl) idx |= 4;
        if (br > lvl) idx |= 2;
        if (bl > lvl) idx |= 1;
        const top = () => interp(x0, y0, tl, x1, y0, tr, lvl);
        const right = () => interp(x1, y0, tr, x1, y1, br, lvl);
        const bottom = () => interp(x0, y1, bl, x1, y1, br, lvl);
        const left = () => interp(x0, y0, tl, x0, y1, bl, lvl);
        const cases = {
          1: [left, bottom], 2: [bottom, right], 3: [left, right], 4: [top, right],
          5: [top, right, left, bottom], 6: [top, bottom], 7: [top, left],
          8: [top, left], 9: [top, bottom], 10: [top, left, bottom, right],
          11: [top, right], 12: [left, right], 13: [bottom, right], 14: [left, bottom],
        };
        const c = cases[idx];
        if (!c) continue;
        for (let k = 0; k < c.length; k += 2) segs.push([c[k](), c[k + 1]()]);
      }
    }
    out.push({ level: lvl, segs });
  }
  return out;
}

// green (low) -> lime -> amber -> brown (high)
export function elevationColor(v, min, max) {
  const t = max > min ? Math.max(0, Math.min(1, (v - min) / (max - min))) : 0.5;
  const stops = [[52, 211, 153], [132, 204, 22], [245, 158, 11], [146, 64, 14]];
  const seg = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(seg));
  const f = seg - i;
  const a = stops[i], b = stops[i + 1];
  const r = Math.round(a[0] + (b[0] - a[0]) * f);
  const g = Math.round(a[1] + (b[1] - a[1]) * f);
  const bl = Math.round(a[2] + (b[2] - a[2]) * f);
  return `rgb(${r},${g},${bl})`;
}
